import React from 'react';
import { Goal, Milestone, Action } from '../types';
import { GoalIcon, ProfileIcon, QuickIcon, ChatIcon, MilestoneIcon, CheckIcon, GrowthIcon } from '../../../icons';

// Fase types - complete geïntegreerde flow
export type GoalPhase = 
  | 'intake'           // Doel is nog draft
  | 'profile'          // Ondernemersprofiel (SelfTests)
  | 'capability'       // Skills assessment
  | 'sparring'         // Gestructureerd gesprek
  | 'milestones'       // Mijlpalen review
  | 'actions'          // Acties genereren
  | 'execution';       // Aan het werk

interface Phase {
  id: GoalPhase;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const PHASES: Phase[] = [
  {
    id: 'intake',
    label: 'Hoofddoel',
    description: 'Doel definiëren en commitment vastleggen',
    icon: <GoalIcon size={16} />
  },
  {
    id: 'profile',
    label: 'Profiel',
    description: 'Type ondernemer en kennisniveau',
    icon: <ProfileIcon size={16} />
  },
  {
    id: 'capability',
    label: 'Skills',
    description: 'Capaciteiten en oplossingen bepalen',
    icon: <QuickIcon size={16} />
  },
  {
    id: 'sparring',
    label: 'Sparring',
    description: 'Strategie en tijdlijn verkennen',
    icon: <ChatIcon size={16} />
  },
  {
    id: 'milestones',
    label: 'Mijlpalen',
    description: 'Tussenresultaten vastleggen',
    icon: <MilestoneIcon size={16} />
  },
  {
    id: 'actions',
    label: 'Acties',
    description: 'Vink taken af om voortgang bij te houden',
    icon: <CheckIcon size={16} />
  },
  {
    id: 'execution',
    label: 'Uitvoering',
    description: 'Werk per week/maand uitvoeren',
    icon: <GrowthIcon size={16} />
  }
];

interface PhaseIndicatorProps {
  goal: Goal;
  milestones: Milestone[];
  actions: Action[];
  hasSelfTestResults: boolean;
  hasCapabilityAssessment: boolean;
  hasSparringSession: boolean;
  onPhaseAction?: (phase: GoalPhase) => void;
}

/**
 * Bepaal de huidige fase op basis van goal status en data
 */
export function detectCurrentPhase(
  goal: Goal,
  milestones: Milestone[],
  actions: Action[],
  hasSelfTestResults: boolean,
  hasCapabilityAssessment: boolean,
  hasSparringSession: boolean
): GoalPhase {
  // Fase 1: Nog niet committed
  if (goal.status !== 'committed') {
    return 'intake';
  }

  // Fase 2: Committed maar geen ondernemersprofiel
  if (!hasSelfTestResults) {
    return 'profile';
  }

  // Fase 3: Profiel done maar geen capability assessment
  if (!hasCapabilityAssessment) {
    return 'capability';
  }

  // Fase 4: Assessment done maar geen sparring
  if (!hasSparringSession) {
    return 'sparring';
  }

  // Fase 5: Sparring done maar geen mijlpalen
  const totalMilestones = milestones.length;
  if (totalMilestones === 0) {
    return 'milestones';
  }

  // Fase 6: Mijlpalen er maar nog geen acties
  const totalActions = actions.length;
  if (totalActions === 0) {
    return 'milestones';  // Stay in milestones if no actions yet
  }

  // Fase 6-7: Acties aanwezig - check of ze afgerond zijn
  const completedActions = actions.filter(a => a.completed);
  const hasUncompletedActions = completedActions.length < totalActions;
  
  // Als er nog onafgeronde acties zijn → actions fase (focus op acties afvinken)
  if (hasUncompletedActions) {
    return 'actions';
  }

  // Fase 7: Alle acties afgerond, aan het werk!
  return 'execution';
}

/**
 * PhaseIndicator
 * 
 * Toont duidelijk in welke fase het doel zich bevindt en wat de volgende stap is.
 * Visueel met progress bar en klikbare fase-badges.
 */
const PhaseIndicator: React.FC<PhaseIndicatorProps> = ({
  goal,
  milestones,
  actions,
  hasSelfTestResults,
  hasCapabilityAssessment,
  hasSparringSession,
  onPhaseAction,
}) => {
  const currentPhase = detectCurrentPhase(
    goal,
    milestones,
    actions,
    hasSelfTestResults,
    hasCapabilityAssessment,
    hasSparringSession
  );

  const currentPhaseIndex = PHASES.findIndex(p => p.id === currentPhase);
  const nextPhase = currentPhaseIndex < PHASES.length - 1 ? PHASES[currentPhaseIndex + 1] : null;

  return (
    <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-5 py-3">
      {/* Fase badges */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-1">
          {PHASES.map((phase, index) => {
            const isCompleted = index < currentPhaseIndex;
            const isCurrent = index === currentPhaseIndex;
            const isUpcoming = index > currentPhaseIndex;

            return (
              <div
                key={phase.id}
                className={`
                  flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium
                  transition-all duration-200
                  ${isCurrent ? 'bg-teal-600 text-white scale-105 shadow-sm' : ''}
                  ${isCompleted ? 'bg-emerald-100 text-emerald-700' : ''}
                  ${isUpcoming ? 'bg-gray-100 text-gray-400' : ''}
                  ${onPhaseAction && !isUpcoming ? 'cursor-pointer hover:scale-105' : ''}
                `}
                onClick={() => !isUpcoming && onPhaseAction?.(phase.id)}
                title={phase.description}
              >
                <span>{phase.icon}</span>
                <span className="hidden sm:inline">{phase.label}</span>
                {isCompleted && <CheckIcon size={12} />}
              </div>
            );
          })}
        </div>

        {/* Voortgang percentage */}
        <span className="text-xs text-gray-500 font-medium">
          {Math.round((currentPhaseIndex / (PHASES.length - 1)) * 100)}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
        <div
          className="bg-gradient-to-r from-emerald-400 to-teal-500 h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${(currentPhaseIndex / (PHASES.length - 1)) * 100}%` }}
        />
      </div>

      {/* Huidige fase en volgende stap */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-gray-700">
            {PHASES[currentPhaseIndex].label}
          </span>
          <span className="text-xs text-gray-500">
            {PHASES[currentPhaseIndex].description}
          </span>
        </div>

        {nextPhase && (
          <button
            onClick={() => onPhaseAction?.(nextPhase.id)}
            className="flex items-center space-x-2 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md"
          >
            <span>→ Volgende:</span>
            <span className="flex items-center space-x-1">
              <span>{nextPhase.icon}</span>
              <span>{nextPhase.label}</span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default PhaseIndicator;
